import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import TeamMember from "@/components/TeamMember";
import { TEAM, DEPARTMENTS, STATS } from "@/data/content";

const FILTER_KEYS = {
  All: "filterAll",
  Leadership: "filterLeadership",
  Creative: "filterCreative",
  "Digital Marketing": "filterDigital",
  "Client Services": "filterClient",
  Analytics: "filterAnalytics",
};

export default function About() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState("All");

  const filtered = useMemo(
    () => (filter === "All" ? TEAM : TEAM.filter((m) => m.department === filter)),
    [filter]
  );

  const filters = ["All", ...DEPARTMENTS];

  const values = [
    { n: "01", t: t("about.v1Title"), b: t("about.v1Body") },
    { n: "02", t: t("about.v2Title"), b: t("about.v2Body") },
    { n: "03", t: t("about.v3Title"), b: t("about.v3Body") },
    { n: "04", t: t("about.v4Title"), b: t("about.v4Body") },
  ];

  const statLabels = [
    t("home.statsPipeline"),
    t("home.statsCountries"),
    t("home.statsRetention"),
    t("home.statsFounded"),
  ];

  return (
    <div className="relative">
      {/* HEADER */}
      <section className="relative pt-24 md:pt-36 pb-20 overflow-hidden">
        <div className="container-x">
          <Reveal><p className="eyebrow mb-6">{t("about.eyebrow")}</p></Reveal>
          <Reveal delay={80}>
            <h1 className="display-h1 max-w-5xl">
              {t("about.heroTitle1")}<br />
              <span className="italic text-clay-400">{t("about.heroTitleItalic")}</span>{t("about.heroTitle2")}
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="body-lg mt-10 max-w-3xl">
              {t("about.heroDescription", { year: 2013 })}
            </p>
          </Reveal>
        </div>
      </section>

      {/* COMPANY SNAPSHOT */}
      <section className="border-y border-white/10 bg-ink-900/50 py-14 md:py-20">
        <div className="container-x grid grid-cols-2 md:grid-cols-4 gap-10">
          {STATS.map((s, i) => (
            <div key={s.label}>
              <div className="font-display text-4xl md:text-5xl text-bone-100">{s.value}</div>
              <div className="mt-2 text-xs uppercase tracking-widest2 text-bone-200/50">
                {statLabels[i]}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* STORY */}
      <section className="py-24 md:py-32">
        <div className="container-x grid grid-cols-1 md:grid-cols-12 gap-16">
          <div className="md:col-span-6">
            <Reveal>
              <blockquote className="font-display text-3xl md:text-4xl leading-[1.2] tracking-tight text-bone-100">
                <span className="text-clay-400">"</span>{t("about.storyQuote")}<span className="text-clay-400">"</span>
              </blockquote>
            </Reveal>
            <Reveal delay={120}>
              <p className="mt-8 text-xs uppercase tracking-widest2 text-bone-200/50">{t("about.storyCaption")}</p>
            </Reveal>
          </div>

          <div className="md:col-span-6 space-y-6 text-bone-200/80 leading-relaxed">
            <Reveal delay={80}><p>{t("about.storyP1")}</p></Reveal>
            <Reveal delay={140}><p>{t("about.storyP2")}</p></Reveal>
            <Reveal delay={200}><p>{t("about.storyP3")}</p></Reveal>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24 md:py-32 bg-ink-900/40 border-y border-white/5">
        <div className="container-x">
          <Reveal><p className="eyebrow mb-6">{t("about.valuesEyebrow")}</p></Reveal>
          <Reveal delay={80}>
            <h2 className="display-h2 max-w-3xl">{t("about.valuesTitle")}</h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mt-16">
            {values.map((v, i) => (
              <Reveal key={v.n} delay={i * 60}>
                <div className="card-surface p-10 h-full">
                  <div className="font-display text-4xl text-clay-400 mb-6">{v.n}</div>
                  <h3 className="display-h3 mb-4">{v.t}</h3>
                  <p className="text-bone-200/75 leading-relaxed">{v.b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="py-24 md:py-32">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-5">
              <Reveal><p className="eyebrow mb-6">{t("about.teamEyebrow")}</p></Reveal>
              <Reveal delay={80}>
                <h2 className="display-h2">{t("about.teamTitle")}</h2>
              </Reveal>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-end">
              <Reveal delay={140}>
                <p className="body-lg">{t("about.teamDescription")}</p>
              </Reveal>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 md:gap-3 mb-12" data-testid="team-filter">
            {filters.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`team-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest2 border transition-colors ${
                  filter === f
                    ? "bg-clay-500 border-clay-500 text-bone-100"
                    : "border-white/15 text-bone-100/70 hover:border-clay-400 hover:text-clay-400"
                }`}
              >
                {t(`about.${FILTER_KEYS[f]}`)}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="team-grid">
            {filtered.map((member, i) => (
              <Reveal key={member.slug} delay={(i % 8) * 40}>
                <TeamMember member={member} testid={`team-member-${member.slug}`} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-ink-900/40 border-t border-white/5">
        <div className="container-x text-center">
          <Reveal>
            <p className="eyebrow justify-center mb-6">{t("about.ctaEyebrow")}</p>
            <h2 className="display-h2 max-w-3xl mx-auto mb-8">{t("about.ctaTitle")}</h2>
            <Link to="/contact" className="btn-primary">
              {t("common.bookCall")}
              <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
